/**
 * The pure ordering→rows mapping behind the direct, ungated build-order write
 * path (005-build-project Phase 5).
 *
 * Given the project's CURRENT work items (each carrying its current
 * `build_order`) and a TARGET sequence of work-item ids (the new order a manual
 * drag — phase 6 — or the agent-live reorder — phase 7 — requested), this
 * computes the `(id, build_order)` updates needed so the persisted order matches
 * the requested sequence. It writes ONLY `work_items.build_order`; it has no
 * dependency concept at all (the resolved Job-C rule: a reorder is a soft
 * preference and NEVER touches `work_item_dependencies`).
 *
 * 05-project-hub-build-order: `build_order` is per-SIBLING-GROUP (the items
 * sharing one `parent_work_item_id`; epics number among top-level siblings,
 * each parent's children number independently from 1). The caller passes
 * exactly ONE sibling group as `currentItems` and a full permutation of it as
 * `sequence` — this mapping itself is group-agnostic.
 *
 * Contract:
 *   - The target `build_order` for each id is its 1-based position in `sequence`
 *     (first → 1, second → 2, …) WITHIN its sibling group. This is the same
 *     ascending convention the shared comparator reads, so a re-read yields the
 *     new order.
 *   - Only CHANGED rows are returned — a row whose current `build_order` already
 *     equals its target position is omitted. Re-applying the current order yields
 *     an empty list (the hook turns that into a no-op).
 *   - Validation: every id in `sequence` MUST belong to the group (be among
 *     `currentItems`). A foreign / unknown id makes the whole mapping INVALID —
 *     `{ valid: false }` — so the hook can refuse to write cross-project rather
 *     than silently writing a foreign id. Duplicate ids are likewise invalid.
 *   - COMPLETENESS: the sequence must permute the WHOLE group — a partial
 *     sequence (anything short of every `currentItems` id) is invalid, or part
 *     of the group would be silently renumbered around stale values.
 *   - A blocked item placed above its blocker is NOT special here — there is no
 *     dependency input. Its new position is written like any other; the
 *     contradiction is shown elsewhere (the inline "Blocked by" note), never
 *     prevented or auto-resolved here.
 *
 * Pure (no React, no IO): unit-testable in isolation.
 */

/** The minimal current-item shape this mapping reads: the id + its current order. */
export interface BuildOrderCurrentItem {
  id: string;
  build_order: number | null;
}

/** One row to write: set `work_items.build_order = build_order` where `id = id`. */
export interface BuildOrderUpdate {
  id: string;
  build_order: number;
}

/**
 * The result of mapping a target sequence onto `(id, build_order)` updates.
 * `valid` is false when the sequence references an id outside the project (or a
 * duplicate) — the caller must NOT write in that case.
 */
export interface BuildOrderUpdatePlan {
  valid: boolean;
  /** Only the rows whose `build_order` actually changes (empty on a no-op). */
  updates: BuildOrderUpdate[];
  /** Set on `valid: false` — a human-readable reason for the refusal. */
  reason?: string;
}

/**
 * Compute the `(id, build_order)` updates that make the project's persisted order
 * match `sequence`. Returns ONLY changed rows; `{ valid: false }` when `sequence`
 * contains an id not in `currentItems` or a duplicate id (never write then).
 */
export function buildOrderUpdates(
  currentItems: ReadonlyArray<BuildOrderCurrentItem>,
  sequence: ReadonlyArray<string>
): BuildOrderUpdatePlan {
  const items = currentItems ?? [];
  const seq = sequence ?? [];

  // Current build_order by id — the universe of this project's items.
  const currentById = new Map<string, number | null>();
  for (const item of items) {
    if (item && typeof item.id === 'string') currentById.set(item.id, item.build_order);
  }

  // Validate: every sequenced id belongs to the project; no duplicates.
  const seen = new Set<string>();
  for (const id of seq) {
    if (typeof id !== 'string' || !currentById.has(id)) {
      return {
        valid: false,
        updates: [],
        reason: `Reorder sequence contains an id that is not in this project: ${String(id)}`
      };
    }
    if (seen.has(id)) {
      return {
        valid: false,
        updates: [],
        reason: `Reorder sequence contains a duplicate id: ${id}`
      };
    }
    seen.add(id);
  }

  // Completeness (05-project-hub-build-order): `build_order` is per-SIBLING-GROUP
  // and the caller passes exactly one group as `currentItems` — so the sequence
  // must be a FULL permutation of that group. A partial sequence would renumber
  // part of the group around stale values; refuse rather than write.
  if (seen.size !== currentById.size) {
    return {
      valid: false,
      updates: [],
      reason:
        'Reorder sequence is incomplete: it must include every item in the sibling group'
    };
  }

  // Target build_order = 1-based position in the sequence; emit only changed rows.
  const updates: BuildOrderUpdate[] = [];
  seq.forEach((id, index) => {
    const target = index + 1;
    if (currentById.get(id) !== target) {
      updates.push({ id, build_order: target });
    }
  });

  return { valid: true, updates };
}
